import { useState, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { SCOPE_UCP } from '../constants/chatGraphCodes.js';

const UCP_PATH_REGEX = /^\/v2\/ucp\/([a-f0-9-]+)(?:\/([^/]+))?/i;

const deriveContextFromPath = (pathname) => {
  const match = pathname.match(UCP_PATH_REGEX);
  if (!match) return null;
  const [, contactId, section = 'overview'] = match;
  if (!contactId) return null;
  return {
    scope: SCOPE_UCP,
    location: { page: 'ucp', section },
    ref_entity: 'CONTACT',
    ref_entity_uuid: contactId,
  };
};

const getTimezoneOffset = () => {
  const offsetMinutes = dayjs().utcOffset();
  const hours = Math.abs(Math.floor(offsetMinutes / 60));
  const sign = offsetMinutes >= 0 ? '+' : '-';
  return `GMT${sign}${hours}`;
};

const resolveUserName = (auth, auth0User) => {
  const userData = auth?.userData;
  const fromStore =
    userData?.first_name ??
    userData?.firstName ??
    auth?.currentUserFirstName ??
    userData?.full_name ??
    userData?.name ??
    ((userData?.first_name || userData?.firstName || userData?.last_name || userData?.lastName)
      ? `${userData?.first_name ?? userData?.firstName ?? ''} ${userData?.last_name ?? userData?.lastName ?? ''}`.trim()
      : '');
  const fromAuth0 = auth0User?.given_name ?? auth0User?.name ?? auth0User?.nickname ?? '';
  return fromStore || fromAuth0;
};

const buildContextPayload = (merged, auth, auth0User) => {
  if (!merged || (typeof merged === 'object' && !Object.keys(merged).length)) return null;
  const entry = merged && typeof merged === 'object' ? merged : {};
  const { entity, entityValue, ref_entity, ref_entity_uuid } = entry;
  const refEntity = ref_entity ?? (entity ? String(entity).toUpperCase().replace(/ID$/, '') : null);
  const refEntityUuid = ref_entity_uuid ?? entityValue ?? null;
  const location =
    entry.location && typeof entry.location === 'object'
      ? {
          page: (entry.location.page || '').toUpperCase(),
          section: (entry.location.section || '').toUpperCase(),
        }
      : { page: '', section: '' };
  const scope = entry.scope ? String(entry.scope).toUpperCase() : null;
  const userName = resolveUserName(auth, auth0User);
  return {
    ...(scope && { scope }),
    location,
    ...(refEntity && { ref_entity: String(refEntity).toUpperCase() }),
    ...(refEntityUuid && { ref_entity_uuid: refEntityUuid }),
    ...(userName && { user_name: userName }),
    timezone: getTimezoneOffset(),
  };
};

/**
 * Orchestrates context injection (Memoria operativa del chat).
 * Manages contextEntries, push/remove/clear, and derives contextForPayload + activeScope.
 * Exposes stable getDerivedContextFromPath and buildContextPayloadForSend for sendMessage.
 */
export const useContextOrchestrator = (authStore, auth0User) => {
  const location = useLocation();
  const [contextEntries, setContextEntries] = useState([]);
  const latestContextEntryRef = useRef(null);

  const syncContextEntry = useCallback((entry) => {
    latestContextEntryRef.current = entry;
  }, []);

  const pushContext = useCallback((entry) => {
    if (!entry || !entry.key) return;
    setContextEntries((prev) => {
      const withoutKey = prev.filter((e) => e.key !== entry.key);
      return [...withoutKey, { ...entry }];
    });
  }, []);

  const removeContext = useCallback((key) => {
    if (!key) return;
    setContextEntries((prev) => prev.filter((e) => e.key !== key));
  }, []);

  const clearContext = useCallback(() => setContextEntries([]), []);

  const contextForPayload = useMemo(() => {
    if (contextEntries.length === 0) return null;
    const merged = contextEntries.reduce((acc, e) => {
      const { key: _k, ...rest } = e;
      return Object.assign(acc, rest);
    }, {});
    return buildContextPayload(merged, authStore, auth0User);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEntries, authStore?.userData, authStore?.currentUserFirstName, auth0User]);

  const activeScope = useMemo(() => {
    const withScope = contextEntries.find((e) => e.scope);
    if (withScope?.scope) return withScope.scope;
    return deriveContextFromPath(location.pathname)?.scope ?? null;
  }, [contextEntries, location.pathname]);

  const getDerivedContextFromPath = useCallback(
    () => deriveContextFromPath(location.pathname),
    [location.pathname]
  );

  const buildContextPayloadForSend = useCallback(
    (merged) => buildContextPayload(merged, authStore, auth0User),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authStore?.userData, authStore?.currentUserFirstName, auth0User]
  );

  return {
    contextEntries,
    latestContextEntryRef,
    pushContext,
    removeContext,
    clearContext,
    syncContextEntry,
    contextForPayload,
    activeScope,
    getDerivedContextFromPath,
    buildContextPayloadForSend,
  };
};
